import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { type ActionType, createAction, createReducer } from 'typesafe-actions';
import 'whatwg-fetch';
import { parseName, type ShapeJSON } from '@takeshape/schema';
import type { Maybe } from '@takeshape/util';
import { assert } from '@takeshape/util';
import { postSchemaRoutine, setUseScratchForPreview, updateSchema } from 'app/branches/actions';
import { getLayerSchema, getScratchLayerSchema, getUserSchema } from 'app/branches/selectors';
import type { BranchesSlice } from 'app/branches/types';
import { showMessageSaga } from 'app/messages/saga';
import { setCurrentProject } from 'app/project/actions';
import startCase from 'lodash/startCase';
import { type editor, Range } from 'monaco-editor';
import { SCRATCH_BRANCH } from '../branches/constants.js';
import { useWorkingCopyStore } from '../branches/store/working-copy-store.js';
import { getSavingErrorMessage } from '../common/utils/handle-error.js';
import { getRoutineSaga } from '../common/utils/new-sagas.js';
import type { ProjectSchemaCompat } from '../compat/types.js';
import { downloadSchema, getSchemaHistoryRoutine, getSchemaVersionRoutine, restoreSchemaRoutine } from './actions.js';
import { getDeltaDecorations, getMonacoCodeEditor } from './selectors.js';
import { downloadSchemaSaga } from './slices/download-schema.js';
import { getSchemaVersionSaga } from './slices/get-schema-version.js';
import { getSchemaHistorySaga } from './slices/get-schema-version-list.js';
import { handleRestoreSchemaSagaFulfilled, restoreSchemaSaga } from './slices/restore-schema.js';
import type { SchemaFilters } from './types.js';
import { getLineNumber } from './util/json-ast.js';

export const SCHEMA_NAME = 'schema';
export const WORKING_COPY_ORIGIN_SERVICES_PANEL = 'WORKING_COPY_SERVICES_PANEL';

export enum SchemaTab {
  Workbench = 'workbench',
  Json = 'json'
}

export enum SchemaView {
  List = 'list',
  Grouped = 'grouped'
}

type IStandaloneCodeEditor = editor.IStandaloneCodeEditor;

export type SchemaSlice = {
  [SCHEMA_NAME]: SchemaState;
} & BranchesSlice;

export type SchemaState = {
  serviceSchemaOpen: boolean;
  introspectDialogServiceId: string | null;
  serviceConfigDialogId?: string;
  serviceConfigDialogOpen: boolean;
  monacoCodeEditor: IStandaloneCodeEditor | null;
  schemaTab: SchemaTab;
  schemaView: SchemaView;
  schemaFilters: SchemaFilters;
  deltaDecorations: string[];
  schemaIndexScrollTo?: SchemaEntry;
};

export const schemaInitialState: SchemaState = {
  serviceSchemaOpen: false,
  introspectDialogServiceId: null,
  serviceConfigDialogOpen: false,
  schemaTab: SchemaTab.Workbench,
  monacoCodeEditor: null,
  schemaView: SchemaView.Grouped,
  schemaFilters: {},
  deltaDecorations: []
};

export type SchemaEntry = {
  type: 'query' | 'mutation' | 'shape';
  name: string;
};

export type AddToUserSchemaPayload = {
  serviceId: string;
} & SchemaEntry;

export const initActionsMultiSelect = createAction('schema/INIT_ACTIONS_MULTI_SELECT')<void>();
export const deleteActions = createAction('schema/DELETE_ACTIONS')<void>();
export const setSchemaTab = createAction('schema/SET_SCHEMA_TAB')<SchemaTab>();
export const highlightInSchema = createAction('schema/HIGHLIGHT_IN_SCHEMA')<SchemaEntry>();
export const setMonacoCodeEditor = createAction('schema/SET_CODE_EDITOR')<IStandaloneCodeEditor>();
export const setSchemaView = createAction('schema/SET_SCHEMA_VIEW')<SchemaView>();
export const setSchemaFilters = createAction('schema/SET_SCHEMA_FILTERS')<SchemaFilters>();
export const clearSchemaFilters = createAction('schema/CLEAR_SCHEMA_FILTERS')<void>();
export const setDeltaDecorations = createAction('schema/SET_DELTA_DECORATIONS')<string[]>();
export const addToUserSchema = createAction('schema/ADD_TO_USER_SCHEMA')<AddToUserSchemaPayload>();
export const scrollSchemaIndex = createAction('schema/SCROLL_SCHEMA_INDEX')<SchemaEntry>();
export const openIntrospectDialog = createAction('schema/OPEN_INTROSPECT_DIALOG')<string | null>();
export const openServiceConfigDialog = createAction('schema/OPEN_SERVICE_CONFIG_DIALOG')<string | undefined>();
export const closeServiceConfigDialog = createAction('schema/CLOSE_SERVICE_CONFIG_DIALOG')<void>();
export const clearServiceConfigId = createAction('schema/CLEAR_SERVICE_CONFIG_ID')<void>();

export type Action =
  | ActionType<typeof setCurrentProject>
  | ActionType<typeof getSchemaVersionRoutine>
  | ActionType<typeof restoreSchemaRoutine>
  | ActionType<typeof initActionsMultiSelect>
  | ActionType<typeof deleteActions>
  | ActionType<typeof setSchemaTab>
  | ActionType<typeof setSchemaView>
  | ActionType<typeof setSchemaFilters>
  | ActionType<typeof clearSchemaFilters>
  | ActionType<typeof highlightInSchema>
  | ActionType<typeof setMonacoCodeEditor>
  | ActionType<typeof setDeltaDecorations>
  | ActionType<typeof scrollSchemaIndex>
  | ActionType<typeof openIntrospectDialog>
  | ActionType<typeof openServiceConfigDialog>
  | ActionType<typeof closeServiceConfigDialog>
  | ActionType<typeof clearServiceConfigId>;

export const reducer = createReducer<SchemaState, Action>(schemaInitialState)
  .handleAction(setCurrentProject, (state) => ({
    ...state,
    schemaFilters: {}
  }))
  .handleAction(setSchemaTab, (state, action) => ({
    ...state,
    schemaTab: action.payload
  }))
  .handleAction(setSchemaView, (state, action) => ({
    ...state,
    schemaView: action.payload
  }))
  .handleAction(setSchemaFilters, (state, action) => ({
    ...state,
    schemaFilters: action.payload
  }))
  .handleAction(clearSchemaFilters, (state) => ({
    ...state,
    schemaFilters: {}
  }))
  .handleAction(setMonacoCodeEditor, (state, action) => ({
    ...state,
    monacoCodeEditor: action.payload
  }))
  .handleAction(setDeltaDecorations, (state, action) => ({
    ...state,
    deltaDecorations: action.payload
  }))
  .handleAction(scrollSchemaIndex, (state, action) => ({
    ...state,
    schemaIndexScrollTo: action.payload
  }))
  .handleAction(openIntrospectDialog, (state, action) => ({
    ...state,
    introspectDialogServiceId: action.payload
  }))
  .handleAction(openServiceConfigDialog, (state, action) => ({
    ...state,
    serviceConfigDialogOpen: true,
    serviceConfigDialogId: action.payload
  }))
  .handleAction(closeServiceConfigDialog, (state) => ({
    ...state,
    serviceConfigDialogOpen: false
  }))
  .handleAction(clearServiceConfigId, (state) => ({
    ...state,
    serviceConfigDialogId: undefined
  }));

export function* handleHighlightInSchema({ payload }: { payload: SchemaEntry }): SagaIterator {
  yield take('schema/SET_CODE_EDITOR');
  const { type, name } = payload;
  const previousDecorations = yield select(getDeltaDecorations);
  const monacoCodeEditor = yield select(getMonacoCodeEditor);
  if (monacoCodeEditor.getValue() === '') {
    return;
  }

  const location = getLineNumber(monacoCodeEditor.getValue(), type, name);

  if (location === undefined) {
    return;
  }

  monacoCodeEditor.revealLineNearTop(location.start.line);
  const decorations = monacoCodeEditor.deltaDecorations(previousDecorations, [
    {
      range: new Range(location.start.line, location.start.column, location.end.line, location.end.column),
      options: {
        isWholeLine: true,
        linesDecorationsClassName: 'takeshape-monaco-highlight'
      }
    }
  ]);
  yield put(setDeltaDecorations(decorations));
}

export function* handleAddToUserSchema({ payload }: { payload: AddToUserSchemaPayload }): SagaIterator {
  const { serviceId, type, name } = payload;
  const userServiceSchema: Maybe<ProjectSchemaCompat> = yield select(getLayerSchema, serviceId);
  const scratchServiceSchema: Maybe<ProjectSchemaCompat> = yield select(getScratchLayerSchema, serviceId);
  const workingUserSchema = useWorkingCopyStore.getState().workingCopyCompat;
  const currentUserSchema = yield select(getUserSchema);
  const schema = workingUserSchema ?? { ...currentUserSchema };
  assert(schema, 'schema exists');

  const serviceSchema = scratchServiceSchema ?? userServiceSchema;

  const serviceConfig = schema.services?.[serviceId];

  assert(serviceSchema, 'service schema exists');
  assert(serviceConfig, 'service config exists');

  const prefixedName = `${serviceConfig.namespace}_${name}`;

  if (type === 'shape') {
    const shape: ShapeJSON = {
      id: prefixedName,
      name: prefixedName,
      title: startCase(prefixedName),
      schema: {
        extends: [{ '@ref': `${serviceConfig.id}:${name}` }]
      }
    };

    const parentInterfaces = serviceSchema.shapes[prefixedName].interfaces;

    if (parentInterfaces) {
      shape.interfaces = parentInterfaces.map((interfaceName) => {
        const { layerId, name } = parseName(schema, interfaceName);
        return `${layerId}:${name}`;
      });
    }

    schema.shapes = {
      ...schema.shapes,
      [prefixedName]: shape
    };
  } else if (type === 'query') {
    schema.queries = {
      ...schema.queries,
      [prefixedName]: serviceSchema.queries[name]
    };
  } else if (type === 'mutation') {
    schema.mutations = {
      ...schema.mutations,
      [prefixedName]: serviceSchema.mutations[name]
    };
  }

  yield call(useWorkingCopyStore.getState().setWorkingCopy, schema, WORKING_COPY_ORIGIN_SERVICES_PANEL);
  yield put(
    postSchemaRoutine.requested(schema, {
      branch: SCRATCH_BRANCH,
      runtime: true
    })
  );
  yield put(setUseScratchForPreview(true));
  yield put(scrollSchemaIndex({ type, name: prefixedName }));
}

export function* schemaSaga(): SagaIterator {
  yield all([
    takeLatest(getSchemaVersionRoutine.requested, getRoutineSaga(getSchemaVersionRoutine, getSchemaVersionSaga)),
    takeLatest(restoreSchemaRoutine.requested, getRoutineSaga(restoreSchemaRoutine, restoreSchemaSaga)),
    takeLatest(restoreSchemaRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Restoring', 'schema'))),
    takeLatest(restoreSchemaRoutine.fulfilled, handleRestoreSchemaSagaFulfilled),
    takeLatest(getSchemaHistoryRoutine.requested, getRoutineSaga(getSchemaHistoryRoutine, getSchemaHistorySaga)),
    takeLatest(downloadSchema, downloadSchemaSaga),
    takeLatest(updateSchema.rejected, showMessageSaga('error', getSavingErrorMessage('Updating', 'schema'))),
    takeLatest(highlightInSchema, handleHighlightInSchema),
    takeLatest(addToUserSchema, handleAddToUserSchema)
  ]);
}
